import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  PlusIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useDebounce } from '../../../hooks/useDebounce';

const MIN_SEARCH_LENGTH = 3;

interface BusinessUser {
  id: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  businessType: string;
  businessStatus: string;
  role: string;
  isSystemSuperAdmin: boolean;
  createdAt: string;
  lastLoginAt?: string;
  places?: {
    id: string;
    name: string;
    role: string;
  }[];
}

interface BusinessUsersResponse {
  businessUsers: BusinessUser[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const BusinessUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const effectiveSearch = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH ? debouncedSearchTerm : '';

  // Buscar usuários empresariais
  const { data: businessUsersData, isLoading: isLoadingBusinessUsers } = useQuery({
    queryKey: ['business-users', effectiveSearch, statusFilter, typeFilter, currentPage, pageSize],
    queryFn: async () => {
      const response = await api.get('/admin/business-users', {
        params: {
          page: currentPage,
          pageSize,
          search: effectiveSearch,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          type: typeFilter !== 'all' ? typeFilter : undefined
        }
      });
      return response.data as BusinessUsersResponse;
    }
  });

  const businessUsers = businessUsersData?.businessUsers || [];
  const pagination = businessUsersData?.pagination;

  // Buscar estatísticas - COMENTADO TEMPORARIAMENTE
  // const { data: stats, isLoading: isLoadingStats } = useQuery({
  //   queryKey: ['business-users-stats'],
  //   queryFn: async () => {
  //     const response = await api.get('/admin/business-users/stats');
  //     return response.data as BusinessUserStats;
  //   }
  // });

  // Função para mudar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Função para aplicar filtros
  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset para primeira página ao aplicar filtros
  };

  if (isLoadingBusinessUsers) { // || isLoadingStats) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Usuários Empresariais</h1>
        <p className="text-gray-600">Gerencie usuários empresariais e suas permissões</p>
      </div>

      {/* Estatísticas - COMENTADO TEMPORARIAMENTE */}
      {/* <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalBusinessUsers || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm font-bold">✓</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeBusinessUsers || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-sm font-bold">✗</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.inactiveBusinessUsers || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-sm font-bold">👑</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Super Admins</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.superAdmins || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-sm font-bold">🏪</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Dono de Cafeteria</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.cafeOwners || 0}</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Filtros e Busca */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">Todos os Status</option>
                <option value="Active">Ativos</option>
                <option value="Inactive">Inativos</option>
                <option value="Pending">Pendentes</option>
                <option value="Suspended">Suspensos</option>
              </select>
            </div>
            <div className="sm:w-48">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">Todos os Tipos</option>
                <option value="CafeOwner">Dono de Cafeteria</option>
                <option value="SystemSuperAdmin">Super Admin</option>
                <option value="SystemAdmin">Admin</option>
                <option value="LegalAdmin">Admin Legal</option>
                <option value="SupportAdmin">Admin Suporte</option>
              </select>
            </div>
            <button 
              onClick={handleApplyFilters}
              className="btn btn-primary"
            >
              Aplicar Filtros
            </button>
            <button 
              onClick={() => navigate('/business-users/new')}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Novo Usuário
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Usuários Empresariais */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo & Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cafeterias Vinculadas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {businessUsers.map((businessUser) => (
                <tr key={businessUser.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{businessUser.fullName}</div>
                        <div className="text-sm text-gray-500">ID: {businessUser.id.slice(0, 8)}...</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{businessUser.email}</div>
                    {businessUser.phoneNumber && (
                      <div className="text-sm text-gray-500">{businessUser.phoneNumber}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        businessUser.businessStatus === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : businessUser.businessStatus === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : businessUser.businessStatus === 'Suspended'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {businessUser.businessStatus === 'Active' ? 'Ativo' : 
                         businessUser.businessStatus === 'Pending' ? 'Pendente' : 
                         businessUser.businessStatus === 'Suspended' ? 'Suspenso' : 'Inativo'}
                      </span>
                      <div className="text-xs text-gray-500">
                        {businessUser.isSystemSuperAdmin && '👑 Super Admin'}
                        {!businessUser.isSystemSuperAdmin && businessUser.role}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {businessUser.places && businessUser.places.length > 0 ? (
                        businessUser.places.map((place) => (
                          <div key={place.id} className="flex items-center text-sm">
                            <LinkIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-gray-900">{place.name}</span>
                            <span className="ml-2 text-xs text-gray-500">({place.role})</span>
                          </div>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">Nenhuma cafeteria vinculada</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {businessUser.lastLoginAt ? new Date(businessUser.lastLoginAt).toLocaleDateString('pt-BR') : 'Nunca'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/business-users/${businessUser.id}/edit`)}
                        className="btn btn-primary flex items-center gap-2"
                        title="Editar usuário"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Editar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{((pagination.page - 1) * pagination.pageSize) + 1}</span> a{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}
                </span> de{' '}
                <span className="font-medium">{pagination.totalCount}</span> resultados
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                
                {/* Páginas numeradas */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          pageNum === pagination.page
                            ? 'bg-emerald-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessUsersPage;
